import type { RouteLocationNormalizedGeneric } from '#vue-router'
import type { OrdersIndexData } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { MAX_PER_PAGE, toPaginationValue, useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useOrdersStore } from '../model/store'

interface OrdersApiOptions {
  route?: RouteLocationNormalizedGeneric
}

/**
 * `GET /admin-api/orders` — the index. Returns `{ orders, meta }`. The `from` / `to` created-at
 * range comes straight from the date-range filter as datetime strings.
 */
export const useOrdersApi = async (options?: OrdersApiOptions): Promise<OrdersIndexData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const route = options?.route || useRoute()
  const store = useOrdersStore()

  const lessonsFrom = store.getFilterLessonsFrom
  const lessonsTo = store.getFilterLessonsTo

  const url = useAdminRoutes('admin.orders.data', pick({
    q: store.getFilterSearch,
    status: store.getFilterStatuses.length ? store.getFilterStatuses : undefined,
    type: store.getFilterType,
    from: store.getFilterFrom || undefined,
    to: store.getFilterTo || undefined,
    // Stringified so `pick` keeps a `0` bound instead of dropping it as falsy.
    lessons_from: lessonsFrom !== null ? String(lessonsFrom) : undefined,
    lessons_to: lessonsTo !== null ? String(lessonsTo) : undefined,
    promocode_id: store.getFilterPromocodeId,
    customer_id: store.getFilterCustomerId,
    per_page: toPaginationValue(route.query?.per_page, store.getPerPage, MAX_PER_PAGE),
    page: toPaginationValue(route.query?.page, store.getCurrentPage),
  }))

  store.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      return null
    }

    const indexData: OrdersIndexData | null = data.data || null

    if (indexData) {
      store.setOrders(indexData.orders)
      store.setMeta(indexData.meta)
    }

    return indexData
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    store.setLoading(false)
  }
}
